import { isRecord } from '@/lib/api'

import type { CredentialValidationResponse } from './types'

interface DemographicValue {
  label: string
  value: string
}

function asDisplayValue(value: unknown): string | null {
  return typeof value === 'string' || typeof value === 'number'
    ? String(value)
    : null
}

function formatValidatedAt(value: string | null): string | null {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

function getDemographics(
  patient: Record<string, unknown> | null | undefined
): DemographicValue[] {
  if (!isRecord(patient)) return []

  const firstName = asDisplayValue(patient.first_name)
  const lastName = asDisplayValue(patient.last_name)
  const phoneNumbers = isRecord(patient.phone_numbers)
    ? patient.phone_numbers
    : undefined
  const values: Array<[string, string | null]> = [
    ['Name', [firstName, lastName].filter(Boolean).join(' ') || null],
    ['Date of Birth', asDisplayValue(patient.date_of_birth)],
    ['Home Phone', asDisplayValue(phoneNumbers?.home)],
    ['Mobile Phone', asDisplayValue(phoneNumbers?.mobile)],
    ['Work Phone', asDisplayValue(phoneNumbers?.work)],
    ['PCP', asDisplayValue(patient.pcp)],
  ]

  return values.flatMap(([label, value]) => (value ? [{ label, value }] : []))
}

interface TestPatientDemographicsProps {
  validation?: CredentialValidationResponse
  title?: string
}

export function TestPatientDemographics({
  validation,
  title = 'Test patient',
}: TestPatientDemographicsProps) {
  const passed = validation?.state === 'passed'
  const demographics = passed ? getDemographics(validation.test_patient) : []
  const validatedAt = passed ? formatValidatedAt(validation.completed_at) : null
  if (demographics.length === 0) return null

  return (
    <div className="w-full rounded-lg bg-muted/50 p-3">
      <p className="text-sm font-medium text-primary-high-contrast">{title}</p>
      <dl className="mt-2 w-full divide-y divide-border">
        {demographics.map((item) => (
          <div
            key={item.label}
            className="grid w-full grid-cols-2 gap-4 py-2 text-sm first:pt-0 last:pb-0">
            <dt className="text-muted-foreground">{item.label}</dt>
            <dd className="break-words text-primary-high-contrast">
              {item.value}
            </dd>
          </div>
        ))}
      </dl>
      {validatedAt ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Validated {validatedAt}
        </p>
      ) : null}
    </div>
  )
}
